import React, { useState, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { LiveProvider, LiveEditor, LiveError, LivePreview } from 'react-live';
import Theme from 'prism-react-renderer/themes/vsDark';
import Tooltip from 'Snippets/Tooltip';
import { is } from 'Common/Utils';
import { useAnimate } from 'Snippets/Animation';
import { Tab, CodeReadOnly, Section, CodePanel, Facade } from './'

Theme.plain.backgroundColor = 'rgb(56, 55, 55)';

const SectionWithCodeProps = {
	title: PropTypes.string,
	context: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	code: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.objectOf(PropTypes.string),
	]),
	readonly: PropTypes.bool,
	triggerId: PropTypes.string,
	triggerIndex: PropTypes.number,
	collapsible: PropTypes.bool,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element])
};

const SectionWithCode = forwardRef(({
	title,
	context,
	code,
	readonly = false,
	triggerId,
	triggerIndex,
	collapsible,
	children,
	...props
}, reference) => {
	const titleID = title.replace(/\s/g, '-').toLowerCase()
	const isObjCode = is.obj(code)
	const keys = isObjCode 
	? Object.keys(code).reduce((result, key) => { 
		result[key] = `${key}-${titleID}`
		return result
	}, {}) : null
	const hash = window.location.hash
	const hasHash = !!hash;
	const defaultActiveKey = isObjCode ? hasHash && hash.includes(`-${titleID}`) ? hash.substr(1) : `${Object.keys(code)[0]}-${titleID}` : null
	const [loading, setLoading] = useState(!hasHash);
	const [ref, show, toggle] = useAnimate({
		in: false,
		animation: {
			enter: {
				height: 'auto',
			},
			exit: { height: '300px' },
		},
	});

	useEffect(() => {
		if (readonly || hasHash) return;
		const delay = setTimeout(() => setLoading(false), 0);
		return () => clearTimeout(delay);
	}, [readonly, hasHash]);

	return (
		<Section ref={reference} title={title} context={context}>
			<div className='section-example'>
				{readonly ? (
					isObjCode ? (
						<Tab
							id={titleID}
							defaultActiveKey={defaultActiveKey}>
							<CodePanel
								triggerId={keys}
								originalId={triggerId}
								triggerIndex={triggerIndex}>
									<Tab.Content>
										{Object.keys(code).map((key) => (
											<Tab.Pane eventKey={`${key}-${titleID}`} key={key}>
												<CodeReadOnly code={code[key]} {...props} language={key === 'css' ? key : 'jsx'} />
											</Tab.Pane>
										))}
									</Tab.Content>
							</CodePanel>
						</Tab>
					) : <CodeReadOnly code={code} {...props} />
				) : isObjCode ? (
					<Tab
						id={titleID}
						defaultActiveKey={defaultActiveKey}>
						<CodePanel
							triggerId={keys}
							originalId={triggerId}
							triggerIndex={triggerIndex}>
							<Facade loading={loading} animated range={[40, 70]} rows={10}>
								<Tab.Content>
									{Object.keys(code).map((key) => (
										<Tab.Pane eventKey={`${key}-${titleID}`} key={key}>
											<LiveProvider code={code[key]} theme={Theme} {...props}>
												<div className='code'>
													<LiveEditor />
												</div>
												<div className='code-result'>
													<LivePreview />
													<LiveError />
												</div>
											</LiveProvider>
										</Tab.Pane>
									))}
								</Tab.Content>
							</Facade>
						</CodePanel>
					</Tab>
				) : (
					<CodePanel
						triggerId={!collapsible || show ? triggerId : undefined}
						triggerIndex={triggerIndex}>
						<Facade loading={loading} animated range={[40, 70]} rows={10}>
							<LiveProvider code={code} theme={Theme} {...props}>
								<div ref={collapsible ? ref : null} className='code'>
									<LiveEditor />
									{!collapsible || show || (
										<div className='collapsible'>
											<Tooltip
												message={`${show ? 'hide' : 'show'} code`}
												placement='top'
												children={
													<span
														className='collapse-trigger'
														onClick={(e) => {
															e.preventDefault();
															toggle();
														}}
														children={
															<h1>
																<i className='mytabworks icon-code'></i>
															</h1>
														}
													/>
												}
											/>
										</div>
									)}
								</div>
								<div className='code-result'>
									<LivePreview />
									<LiveError />
								</div>
							</LiveProvider>
						</Facade>
					</CodePanel>
				)}
				{children}
			</div>
		</Section>
	);
});

SectionWithCode.propTypes = SectionWithCodeProps;

export default SectionWithCode;
